import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import socketService from '../services/socket';
import { toast } from 'react-hot-toast';

const SocketContext = createContext({});

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { user, isAuthenticated, socketToken } = useAuth();
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});

  useEffect(() => {
    if (isAuthenticated && user && socketToken) {
      // Connect to socket with token (socket.io can't use cookies)
      socketService.connect(socketToken);

      // Setup event listeners
      socketService.addEventListener('connection', handleConnection);
      socketService.addEventListener('proposal-received', handleProposalReceived);
      socketService.addEventListener('proposal-status-updated', handleProposalStatusUpdate);
      socketService.addEventListener('proposal-update', handleProposalUpdate);
      socketService.addEventListener('message-received', handleMessageReceived);
      socketService.addEventListener('user-typing', handleUserTyping);
      socketService.addEventListener('user-stopped-typing', handleUserStoppedTyping);
      socketService.addEventListener('project-updated', handleProjectUpdate);
      socketService.addEventListener('deliverable-uploaded', handleDeliverableUploaded);
      socketService.addEventListener('payment-updated', handlePaymentUpdate);

      return () => {
        // Cleanup listeners
        socketService.removeEventListener('connection', handleConnection);
        socketService.removeEventListener('proposal-received', handleProposalReceived);
        socketService.removeEventListener('proposal-status-updated', handleProposalStatusUpdate);
        socketService.removeEventListener('proposal-update', handleProposalUpdate);
        socketService.removeEventListener('message-received', handleMessageReceived);
        socketService.removeEventListener('user-typing', handleUserTyping);
        socketService.removeEventListener('user-stopped-typing', handleUserStoppedTyping);
        socketService.removeEventListener('project-updated', handleProjectUpdate);
        socketService.removeEventListener('deliverable-uploaded', handleDeliverableUploaded);
        socketService.removeEventListener('payment-updated', handlePaymentUpdate);
        
        // Disconnect socket
        socketService.disconnect();
      };
    }
  }, [isAuthenticated, user, socketToken]);

  // Event handlers
  const handleConnection = (data) => {
    setConnected(data.connected);
    if (data.connected) {
      console.log('✅ Realtime updates connected');
    } else {
      console.log('❌ Realtime updates disconnected');
    }
  };

  const handleProposalReceived = (data) => {
    if (user?.role === 'bedrijf') {
      toast.success(`Nieuwe propositie ontvangen voor ${data.projectTitle}`, {
        duration: 5000,
        position: 'top-right',
      });
      addNotification({
        type: 'proposal',
        message: `Nieuwe propositie van ${data.creatorName}`,
        data,
        timestamp: new Date(),
      });
    }
  };

  const handleProposalStatusUpdate = (data) => {
    if (user?.role === 'creator') {
      const statusMessages = {
        accepted: '✅ Je propositie is geaccepteerd!',
        rejected: '❌ Je propositie is afgewezen',
        under_review: '👀 Je propositie wordt bekeken',
      };
      
      toast(statusMessages[data.status] || 'Propositie status geüpdatet', {
        duration: 5000,
        position: 'top-right',
      });
      
      addNotification({
        type: 'proposal-status',
        message: statusMessages[data.status],
        data,
        timestamp: new Date(),
      });
    }
  };

  const handleProposalUpdate = (data) => {
    // Trigger refresh in relevant components
    window.dispatchEvent(new CustomEvent('proposal-update', { detail: data }));
  };

  const handleMessageReceived = (data) => {
    toast(`💬 Nieuw bericht van ${data.senderName}`, {
      duration: 4000,
      position: 'bottom-right',
    });
    
    addNotification({
      type: 'message',
      message: `Nieuw bericht in ${data.projectTitle || 'conversatie'}`,
      data,
      timestamp: new Date(),
    });
    
    // Trigger refresh in messages component
    window.dispatchEvent(new CustomEvent('message-received', { detail: data }));
  };

  const handleUserTyping = (data) => {
    setTypingUsers(prev => ({
      ...prev,
      [data.projectId]: {
        ...prev[data.projectId],
        [data.userId]: data.userName,
      }
    }));
  };

  const handleUserStoppedTyping = (data) => {
    setTypingUsers(prev => {
      const updated = { ...prev };
      if (updated[data.projectId]) {
        delete updated[data.projectId][data.userId];
        if (Object.keys(updated[data.projectId]).length === 0) {
          delete updated[data.projectId];
        }
      }
      return updated;
    });
  };

  const handleProjectUpdate = (data) => {
    toast.info(`Project "${data.projectTitle}" is bijgewerkt`, {
      duration: 3000,
      position: 'top-center',
    });
    
    // Trigger refresh in projects component
    window.dispatchEvent(new CustomEvent('project-update', { detail: data }));
  };

  const handleDeliverableUploaded = (data) => {
    if (user?.role === 'bedrijf') {
      toast.success(`Nieuwe deliverable geüpload voor ${data.projectTitle}`, {
        duration: 5000,
        position: 'top-right',
      });
      
      addNotification({
        type: 'deliverable',
        message: `Nieuwe content beschikbaar voor review`,
        data,
        timestamp: new Date(),
      });
    }
    
    // Trigger refresh
    window.dispatchEvent(new CustomEvent('deliverable-update', { detail: data }));
  };

  const handlePaymentUpdate = (data) => {
    const paymentMessages = {
      deposit_paid: '💰 Aanbetaling ontvangen',
      final_paid: '💰 Eindbetalding ontvangen',
      payment_released: '💸 Betaling vrijgegeven',
    };
    
    toast.success(paymentMessages[data.status] || 'Betaling geüpdatet', {
      duration: 5000,
      position: 'top-right',
    });
    
    addNotification({
      type: 'payment',
      message: paymentMessages[data.status],
      data,
      timestamp: new Date(),
    });
    
    // Trigger refresh
    window.dispatchEvent(new CustomEvent('payment-update', { detail: data }));
  };

  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev].slice(0, 50)); // Keep last 50
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const markNotificationAsRead = (index) => {
    setNotifications(prev => 
      prev.map((notif, i) => 
        i === index ? { ...notif, read: true } : notif
      )
    );
  };

  // Public methods
  const joinProject = (projectId) => {
    socketService.joinProject(projectId);
  };

  const leaveProject = (projectId) => {
    socketService.leaveProject(projectId);
  };

  const sendProposal = (data) => {
    socketService.sendProposal(data);
  };

  const updateProposalStatus = (data) => {
    socketService.updateProposalStatus(data);
  };

  const sendMessage = (data) => {
    socketService.sendMessage(data);
  };

  const startTyping = (projectId) => {
    socketService.startTyping(projectId);
  };

  const stopTyping = (projectId) => {
    socketService.stopTyping(projectId);
  };

  const value = {
    connected,
    notifications,
    typingUsers,
    clearNotifications,
    markNotificationAsRead,
    joinProject,
    leaveProject,
    sendProposal,
    updateProposalStatus,
    sendMessage,
    startTyping,
    stopTyping,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};